import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: '', tenant_code: '', startDate: '', endDate: '' });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 30;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (filters.action) params.append('action', filters.action);
      if (filters.tenant_code) params.append('tenant_code', filters.tenant_code);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      const res = await api.get(`/audit?${params}`);
      setLogs(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch {} finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleFilter = (e) => { e.preventDefault(); setPage(1); };
  const clearFilters = () => { setFilters({ action: '', tenant_code: '', startDate: '', endDate: '' }); setPage(1); };
  const totalPages = Math.ceil(total / limit);

  const actionColors = {
    tenant_created: 'bg-green-100 text-green-700',
    tenant_updated: 'bg-blue-100 text-blue-700',
    tenant_activated: 'bg-emerald-100 text-emerald-700',
    tenant_suspended: 'bg-amber-100 text-amber-700',
    tenant_deleted: 'bg-red-100 text-red-700',
    credential_reset: 'bg-purple-100 text-purple-700',
    subscription_renewed: 'bg-indigo-100 text-indigo-700',
    settings_updated: 'bg-gray-100 text-gray-600',
    platform_user_created: 'bg-teal-100 text-teal-700',
    platform_user_deleted: 'bg-rose-100 text-rose-700',
  };

  const actionIcons = {
    tenant_created: '🏢', tenant_updated: '✏️', tenant_activated: '✅', tenant_suspended: '⏸️',
    tenant_deleted: '🗑️', credential_reset: '🔑', subscription_renewed: '🔄', settings_updated: '⚙️',
    platform_user_created: '👤', platform_user_deleted: '👤',
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Audit Logs</h2>
        <p className="text-sm text-gray-500 mt-0.5">Platform-wide activity trail — all actions are logged for accountability</p>
      </div>

      {/* Filters */}
      <form onSubmit={handleFilter} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Action</label>
            <select value={filters.action} onChange={e => setFilters(p => ({ ...p, action: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
              <option value="">All Actions</option>
              {['tenant_created', 'tenant_updated', 'tenant_activated', 'tenant_suspended', 'tenant_deleted', 'credential_reset', 'subscription_renewed', 'settings_updated', 'platform_user_created', 'platform_user_deleted'].map(a => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Tenant Code</label>
            <input value={filters.tenant_code} onChange={e => setFilters(p => ({ ...p, tenant_code: e.target.value }))} placeholder="e.g. clienta" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
            <input type="date" value={filters.startDate} onChange={e => setFilters(p => ({ ...p, startDate: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
            <input type="date" value={filters.endDate} onChange={e => setFilters(p => ({ ...p, endDate: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </div>
          <div className="flex items-end space-x-2">
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors">Filter</button>
            <button type="button" onClick={clearFilters} className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">Clear</button>
          </div>
        </div>
      </form>

      {/* Results */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm text-gray-500">Showing {logs.length} of {total} logs</span>
          <div className="flex items-center space-x-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-30 hover:bg-gray-50">&laquo; Prev</button>
            <span className="text-xs text-gray-500">Page {page} / {totalPages || 1}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-3 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-30 hover:bg-gray-50">Next &raquo;</button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">No audit logs found</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {logs.map((log, i) => (
              <div key={log.id || i} className="px-4 py-3 hover:bg-gray-50/50 transition-colors">
                <div className="flex items-start space-x-3">
                  <div className="text-lg mt-0.5">{actionIcons[log.action] || '📋'}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${actionColors[log.action] || 'bg-gray-100 text-gray-600'}`}>{(log.action || '').replace(/_/g, ' ')}</span>
                      {log.tenant_code && <span className="text-xs text-gray-400 font-mono bg-gray-50 px-1.5 py-0.5 rounded">{log.tenant_code}</span>}
                    </div>
                    {log.details && <p className="text-sm text-gray-700 mt-1 truncate">{typeof log.details === 'string' ? log.details : JSON.stringify(log.details)}</p>}
                    <div className="flex items-center space-x-3 mt-1 text-xs text-gray-400">
                      <span>By: {log.performed_by || 'system'}</span>
                      <span>&middot;</span>
                      <span>{new Date(log.created_at).toLocaleString()}</span>
                      {log.ip_address && <><span>&middot;</span><span>IP: {log.ip_address}</span></>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
